//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import {
  PanelContext, type PanelContextValue, type PanelEntry, type PanelSpec
} from '@/contexts/PanelContext';
import {
  cascadeOffset, clampSize, clampToReach, resolveAnchor, type PanelBounds,
  type PanelRect
} from '@/components/Panels/panelGeometry';
import {
  useFloatingPanelViewport
} from '@/components/Panels/panelViewport';
import { useAuth } from '@/hooks/useAuth';
import { usePersistentState } from '@/hooks/usePersistentState';

interface PanelProviderProps {
  children: React.ReactNode;
}

// Until the host measures the main content region, panels have nowhere to go.
// A zero-size default rather than a guess: clamping against a wrong guess
// would move panels twice, visibly.
const EMPTY_BOUNDS: PanelBounds = {
  left: 0, top: 0, width: 0, height: 0
};

const sameBounds = (a: PanelBounds, b: PanelBounds): boolean =>
  a.left === b.left && a.top === b.top &&
  a.width === b.width && a.height === b.height;

const sameRect = (a: PanelRect, b: PanelRect): boolean =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

/**
 * Owns the floating-panel registry: which panels a client has declared, which
 * of them are mounted, and where each one sits, and the policy that puts one
 * on screen with no gesture behind it -- a session restore, a preload.
 *
 * Geometry outlives a panel so that closing and reopening returns it to where
 * the user last put it; it is stored per user, which also keeps one user's
 * layout off a shared workstation's next login.  Which panels were open is
 * session-scoped instead, so a reload restores the working arrangement.  Both
 * are keyed by user, so the next person to sign in on this tab starts quiet --
 * the same person signing back in picks up where they left off.
 */
const PanelProvider = ({ children }: PanelProviderProps) => {
  const { userProfile } = useAuth();
  const username = userProfile?.username ?? null;
  const canFloat = useFloatingPanelViewport();
  const [panels, setPanels] = useState<PanelEntry[]>([]);
  const [bounds, setBoundsState] = useState<PanelBounds>(EMPTY_BOUNDS);
  const [geometry, setGeometry] =
    usePersistentState<Record<string, PanelRect>>('panelGeometry', {});
  const [openIds, setOpenIds] =
    usePersistentState<string[]>('panelOpenIds', [], true);

  // Specs are read inside callbacks that must not be re-created on every
  // change, so the ref is what those read. The state copy is the same map,
  // and exists because a launcher draws a registered panel's icon and title
  // during render: a ref alone would leave it drawing the frame before.
  const specs = useRef<ReadonlyMap<string, PanelSpec>>(new Map());
  const [registered, setRegistered] =
    useState<ReadonlyMap<string, PanelSpec>>(specs.current);
  // What activated each open panel, so dismissing it can hand focus back.
  // A ref rather than part of an entry: it is a live DOM node, nothing
  // renders from it, and an entry is plain data that gets persisted.
  const invokers = useRef(new Map<string, HTMLElement>());
  // The live registry, and the authority every mutator reads and writes. It is
  // never re-synced from `panels` during render: each mutator writes it before
  // dispatching, so state can only ever be equal to it or one dispatch behind.
  const panelsRef = useRef<PanelEntry[]>(panels);
  // The live bounds, for the same reason: `openPanel` places a new panel
  // against whatever `setBounds` was last told, even within the same batch.
  const boundsRef = useRef(bounds);
  // The live geometry, so `initialRect` and the callbacks built on it keep
  // one identity across geometry writes. Read through state instead, every
  // move or resize would mint a fresh `mountPanel`, and the restore-and-
  // preload effect keyed on it would re-run on every drag. Mutators write it
  // eagerly; refreshing it every render, the way usePersistentState refreshes
  // its own fallback ref, catches the writes they cannot see -- the store
  // hydrating when the signed-in user arrives, in the same render the restore
  // reads the stored rects from. Refreshed in an effect instead, it would
  // still hold the previous user's rects when that restore ran.
  const geometryRef = useRef(geometry);
  geometryRef.current = geometry;
  // Which user each registered panel has had its restore-or-preload decision
  // made for, so the decision is made once per panel per sign-in.
  const settled = useRef(new Map<string, string>());
  // The stacking counter: every open or raise takes the next value, so the
  // entry holding the largest is the frontmost. A counter rather than
  // Date.now() because two raises can land in one millisecond.
  const stackSeq = useRef(0);

  /**
   * The one way the registry changes.
   *
   * The next array is computed from the ref and assigned back before React is
   * handed a plain value -- never an updater. Two mutators called from one
   * handler are batched with no render between them, so a mutator reading
   * `panels` from state would compute against a rect an earlier call in the
   * same batch had already replaced: a resize after a move committed the
   * pre-move origin, and a move on a panel opened in the same batch found no
   * panel at all. Mixing the two styles reintroduces exactly that, since an
   * eager read cannot see a queued updater's result.
   */
  const commitPanels = useCallback(
    (next: (prev: PanelEntry[]) => PanelEntry[]) => {
      const updated = next(panelsRef.current);
      panelsRef.current = updated;
      setPanels(updated);
    }, []);

  // Logging out is a client-side navigation and the host renders outside the
  // router, so nothing takes a panel down on its own: one left standing shows
  // the previous user's content over the login form. The specs stay, since
  // every client re-registers on mount.
  // The decisions go with them: the panels they were made about are gone, so
  // a latch held over would leave this user's next session with a remembered
  // panel and nothing to restore it.
  useEffect(() => {
    if (userProfile) return;
    settled.current.clear();
    commitPanels((prev) => prev.length === 0 ? prev : []);
  }, [userProfile, commitPanels]);

  const setBounds = useCallback((next: PanelBounds) => {
    // Every caller reporting the region already on record -- the host
    // re-measuring, or a second caller echoing what the first already
    // published -- costs nothing: re-clamping every entry into a rect that
    // has not changed would only re-render every panel for no visible
    // effect. `EMPTY_BOUNDS` never equals a real measurement, so the first
    // one still commits.
    if (sameBounds(boundsRef.current, next)) return;
    boundsRef.current = next;
    setBoundsState(next);
    // A viewport change can strand a panel that was placed against an edge
    // that has since moved inward. Reached rather than fully enclosed, so a
    // panel the user parked half off-screen stays parked: the shrink pulls it
    // in only as far as keeping hold of it requires. Deliberately not
    // persisted: the user did not choose this position, and saving it would
    // let a shrink and regrow walk the panel permanently into the corner.
    // Each entry's own minimum rides along, so a regrown region hands back
    // the floor a shrink took.
    //
    // An entry the clamp leaves where it was keeps its identity, and a
    // change that moves nothing hands back `prev` itself: most bounds
    // changes -- every frame of a window grow, most frames of a shrink --
    // strand no panel, and rebuilding the registry for them would re-render
    // every panel body once per resize frame.
    commitPanels((prev) => {
      let moved = false;
      const clamped = prev.map((entry) => {
        const rect = clampToReach(entry.rect, next, entry.spec.minSize);
        if (sameRect(rect, entry.rect)) return entry;
        moved = true;
        return { ...entry, rect };
      });
      return moved ? clamped : prev;
    });
  }, [commitPanels]);

  // Replaced rather than mutated, so the ref and the state stay one map and a
  // consumer cannot read a registration React has not been told about. A
  // re-register of the same spec is the common case -- a client's effect
  // re-running -- and must not render.
  const registerPanel = useCallback((spec: PanelSpec) => {
    if (specs.current.get(spec.id) === spec) return;
    const next = new Map(specs.current);
    next.set(spec.id, spec);
    specs.current = next;
    setRegistered(next);
  }, []);

  // Withdraws a registration and the panel with it. The entry has to go too:
  // an unregistered surface has no launcher and no Tools row, so a panel
  // left mounted would keep rendering a live frame of it that nothing on
  // screen can reach or close.
  const unregisterPanel = useCallback((id: string) => {
    if (!specs.current.has(id)) return;
    const next = new Map(specs.current);
    next.delete(id);
    specs.current = next;
    setRegistered(next);
    commitPanels((prev) => prev.filter((entry) => entry.spec.id !== id));
    setOpenIds((prev) => prev.filter((openId) => openId !== id));
    // The restore-or-preload decision goes with the registration it was made
    // about. A surface an admin turned off and back on is a panel to decide
    // about afresh -- kept, the decision would leave it unable to preload
    // again until the user signed out. Withdrawal is the only thing that
    // reopens the question: closing a panel touches neither the registration
    // nor this map, so a Stop stays a Stop.
    settled.current.delete(id);
  }, [commitPanels, setOpenIds]);

  const getPanelSpec = useCallback(
    (id: string) => registered.get(id), [registered]);

  // Where a panel with no remembered geometry belongs: its anchor, stepped off
  // any corner already occupied. Shared by open and reset so that resetting
  // two panels with the same anchor cannot stack them.
  const anchoredRect = useCallback(
    (spec: PanelSpec, taken: PanelRect[], area: PanelBounds): PanelRect => {
      const size = clampSize(spec.size, spec.minSize, area);
      const at = resolveAnchor(spec.anchor, size, area);
      return cascadeOffset({ ...at, ...size }, taken, area);
    }, []);

  const initialRect = useCallback(
    (spec: PanelSpec, taken: PanelRect[]): PanelRect => {
      const area = boundsRef.current;
      const stored = geometryRef.current[spec.id];
      if (stored) return clampToReach(stored, area, spec.minSize);

      return anchoredRect(spec, taken, area);
    }, [anchoredRect]);

  // Put a panel on screen. `takesFocus` is the whole difference between the
  // two ways that happens: the user asking for it, and the session putting
  // back what it was showing before.
  const showPanel = useCallback((id: string, takesFocus: boolean) => {
    const spec = specs.current.get(id);
    if (!spec) return;

    // Whatever the user activated to get here, captured now because now is
    // the only time it holds focus: the panel takes focus as soon as it
    // renders, so by the first effect this is already gone. A restore has no
    // gesture behind it, so there is nothing to record and nothing that
    // dismissing it should hand focus back to.
    const from = takesFocus ? document.activeElement : null;
    if (from instanceof HTMLElement && from !== document.body)
      invokers.current.set(id, from);

    commitPanels((prev) => {
      const existing = prev.find((entry) => entry.spec.id === id);
      // A user-driven open bumps the epoch even when the panel is already
      // open: state and takesFocus are unchanged then, and the epoch is what
      // tells the panel that a new request wants focus moved into it.
      const opened: PanelEntry = existing ?
        {
          ...existing,
          state: 'open',
          takesFocus,
          focusEpoch: takesFocus ?
            existing.focusEpoch + 1 : existing.focusEpoch,
          stackOrder: ++stackSeq.current
        } :
        {
          spec,
          state: 'open',
          rect: initialRect(spec, prev.map((entry) => entry.rect)),
          takesFocus,
          focusEpoch: takesFocus ? 1 : 0,
          stackOrder: ++stackSeq.current
        };
      // The fresh stackOrder is what raises it. An existing entry is replaced
      // in place rather than re-appended: reordering the array would move the
      // panel's DOM node, which reloads a plugin panel's iframe.
      return existing ?
        prev.map((entry) => entry.spec.id === id ? opened : entry) :
        [...prev, opened];
    });
    setOpenIds((prev) => prev.includes(id) ? prev : [...prev, id]);
  }, [commitPanels, initialRect, setOpenIds]);

  const openPanel = useCallback(
    (id: string) => showPanel(id, true), [showPanel]);

  // Mount without showing, for a surface that must accumulate while parked.
  // Deliberately not `openPanel` followed by `iconifyPanel`: that would flash
  // the panel onto the screen, hand it focus, and record whatever happened to
  // be focused as the thing to hand focus back to.
  //
  // A panel already mounted is left exactly as it is -- a preload that
  // arrived after the user opened or moved one must not park what they are
  // looking at, or pull it back to its anchor.
  const mountPanel = useCallback((id: string) => {
    const spec = specs.current.get(id);
    if (!spec) return;
    commitPanels((prev) => {
      if (prev.some((entry) => entry.spec.id === id)) return prev;
      // Behind everything already on screen: nothing asked for this one, so
      // it has no claim on the front of the stack -- stackOrder zero sits
      // under every value the counter has issued.
      return [...prev, {
        spec,
        state: 'iconified',
        rect: initialRect(spec, prev.map((entry) => entry.rect)),
        takesFocus: false,
        focusEpoch: 0,
        stackOrder: 0
      }];
    });
  }, [commitPanels, initialRect]);

  const restorePanel = useCallback(
    (id: string) => showPanel(id, false), [showPanel]);

  /**
   * The whole of what puts a panel on screen without being asked: reopening
   * what the session was showing, and parking a surface that has to
   * accumulate from sign-in. Owned here rather than by each client, so a
   * client cannot lose either of them by omission, and so the two are ordered
   * against each other once -- restore first, since a panel that is both
   * remembered and preloading belongs open rather than parked.
   *
   * The wait for a username is not incidental. The remembered set and the
   * stored geometry are both per user and both re-read during the render the
   * profile arrives in, so a panel restored ahead of that lands on its anchor
   * instead of where its user left it, and their next drag overwrites the
   * position they chose. A panel restored with no user at all would be a
   * panel over the login page belonging to nobody.
   *
   * Decided once per panel per username rather than once per mount. Logging
   * out is a client-side navigation, so the next user arrives with this
   * provider still mounted; and within one session any registration change --
   * a plugin surface an admin withdrew -- re-runs this over the whole
   * registry, where a second decision would silently undo a Stop the user
   * made on some unrelated panel.
   */
  useEffect(() => {
    if (!username) return;
    // Registration order, so two remembered panels come back stacked the way
    // their clients registered them rather than in whatever order an effect
    // happened to fire.
    for (const [id, spec] of registered) {
      if (settled.current.get(id) === username) continue;
      // Held back rather than decided: this panel's launcher is not drawn at
      // this width, so a restore would strand it, and a window grown past
      // the breakpoint re-runs this with the decision still owed.
      if (spec.hasDesktopOnlyLauncher && !canFloat) continue;
      settled.current.set(id, username);
      // Already on screen or already parked -- the user opened it themselves,
      // most likely -- so there is nothing to put back and nothing a preload
      // may park out from under them.
      if (panelsRef.current.some((entry) => entry.spec.id === id)) continue;
      if (openIds.includes(id)) restorePanel(id);
      else if (spec.preload) mountPanel(id);
    }
  }, [registered, username, canFloat, openIds, restorePanel, mountPanel]);

  const iconifyPanel = useCallback((id: string) => {
    commitPanels((prev) => prev.map((entry) => entry.spec.id === id ?
      { ...entry, state: 'iconified' } : entry));
    setOpenIds((prev) => prev.filter((openId) => openId !== id));
  }, [commitPanels, setOpenIds]);

  const closePanel = useCallback((id: string) => {
    commitPanels((prev) => prev.filter((entry) => entry.spec.id !== id));
    setOpenIds((prev) => prev.filter((openId) => openId !== id));
  }, [commitPanels, setOpenIds]);

  // Read after the panel is gone, when the caller can tell whether focus was
  // orphaned by it. Null once the recorded element has left the document --
  // the header remounts on every route change, so a launcher captured under
  // a previous page is a node nothing can focus.
  const getPanelInvoker = useCallback((id: string) => {
    const invoker = invokers.current.get(id);
    if (!invoker?.isConnected) return null;
    return invoker;
  }, []);

  // A raise writes a fresh stackOrder and never reorders the array: React
  // moves a reordered keyed child's DOM node, and a node moved during the
  // press silently voids the pointer capture the title bar just took -- the
  // drag that raised a buried panel would stop following the pointer the
  // moment it left the bar, with no lostpointercapture to say so.
  //
  // Raising the panel already in front is the overwhelmingly common call:
  // this runs on every pointer press anywhere inside a panel. Returning
  // `prev` unchanged is what keeps those presses from costing anything -- a
  // fresh array would hand every consumer of the registry a new context
  // value on every click and keystroke, the header included.
  const raisePanel = useCallback((id: string) => {
    commitPanels((prev) => {
      const entry = prev.find((candidate) => candidate.spec.id === id);
      if (!entry) return prev;
      const isInFront = prev.every((candidate) =>
        candidate === entry || candidate.stackOrder < entry.stackOrder);
      if (isInFront) return prev;
      const raised = { ...entry, stackOrder: ++stackSeq.current };
      return prev.map((candidate) =>
        candidate === entry ? raised : candidate);
    });
  }, [commitPanels]);

  // Geometry is written to storage as well as state: it is the one part of a
  // panel the user expects to find again tomorrow.
  const commitRect = useCallback((id: string, rect: PanelRect) => {
    const entry = panelsRef.current.find(
      (candidate) => candidate.spec.id === id);
    if (!entry) return;
    const area = boundsRef.current;
    // The spec's own floor rides the clamp, or a move would persist a size a
    // shrunken viewport forced below it.
    const clamped = clampToReach(rect, area, entry.spec.minSize);
    commitPanels((prev) => prev.map((candidate) =>
      candidate.spec.id === id ? { ...candidate, rect: clamped } : candidate));
    // An unmeasured axis constrains nothing, so a rect derived from one is a
    // placeholder rather than a position the user chose. Persisting it would
    // outrank the panel's anchor on every later open.
    if (area.width > 0 && area.height > 0) {
      geometryRef.current = { ...geometryRef.current, [id]: clamped };
      setGeometry(geometryRef.current);
    }
  }, [commitPanels, setGeometry]);

  const movePanel = useCallback((id: string, x: number, y: number) => {
    const entry = panelsRef.current.find(
      (candidate) => candidate.spec.id === id);
    if (!entry) return;
    commitRect(id, { ...entry.rect, x, y });
  }, [commitRect]);

  const resizePanel = useCallback((id: string, rect: PanelRect) => {
    const entry = panelsRef.current.find(
      (candidate) => candidate.spec.id === id);
    if (!entry) return;
    // The floor again, behind whatever the caller already applied: the menu's
    // size presets are fractions of the region and answer to nothing else.
    const bounded = clampSize(rect, entry.spec.minSize, boundsRef.current);
    commitRect(id, { x: rect.x, y: rect.y, ...bounded });
  }, [commitRect]);

  const resetPanelGeometry = useCallback((id: string) => {
    const spec = specs.current.get(id);
    if (!spec) return;
    const scrubbed = { ...geometryRef.current };
    delete scrubbed[id];
    geometryRef.current = scrubbed;
    setGeometry(scrubbed);
    const area = boundsRef.current;
    commitPanels((prev) => {
      const taken = prev.filter((entry) => entry.spec.id !== id)
        .map((entry) => entry.rect);
      const rect = anchoredRect(spec, taken, area);
      return prev.map((entry) => entry.spec.id === id ?
        { ...entry, rect } : entry);
    });
  }, [anchoredRect, commitPanels, setGeometry]);

  const isPanelOpen = useCallback((id: string) => panels.some(
    (entry) => entry.spec.id === id && entry.state === 'open'), [panels]);

  const isPanelMounted = useCallback((id: string) => panels.some(
    (entry) => entry.spec.id === id), [panels]);

  const value = useMemo((): PanelContextValue => ({
    panels, bounds, setBounds, registerPanel, unregisterPanel, getPanelSpec,
    getPanelInvoker, openPanel, mountPanel, restorePanel, iconifyPanel,
    closePanel, raisePanel, movePanel, resizePanel, resetPanelGeometry,
    isPanelOpen, isPanelMounted
  }), [panels, bounds, setBounds, registerPanel, unregisterPanel, getPanelSpec,
    getPanelInvoker, openPanel, mountPanel, restorePanel, iconifyPanel,
    closePanel, raisePanel, movePanel, resizePanel, resetPanelGeometry,
    isPanelOpen, isPanelMounted]);

  return (
    <PanelContext.Provider value={value}>
      {children}
    </PanelContext.Provider>
  );
};

export default PanelProvider;
